<script setup lang="ts" name="AdminPortCallsFilters">
import { computed } from 'vue';
import { useVModel } from '@vueuse/core';

import { useActiveFilters } from '~/features/useActiveFilters';

import type {
  IAdminPortCallsFilters,
  IAdminPortCallsQuery,
  IFilter,
  IOption,
} from '~/types';

import FilterCustomPeriodSelect from './FilterCustomPeriodSelect.vue';
import FilterMenuSelectMultiple from './FilterMenuSelectMultiple.vue';
import FiltersLayout from './FiltersLayout.vue';
import FilterTag from './FilterTag.vue';

const props = defineProps<{
  modelValue: IAdminPortCallsQuery;
  options: IAdminPortCallsFilters;
}>();

const emit = defineEmits<{
  'update:modelValue': [];
}>();

const query = useVModel(props, 'modelValue', emit);

const { activeFilters, clearFilter, clearQuery } = useActiveFilters(query);

const matchesSelection = <T extends IOption>(
  selectedFilters: T[],
  options: string[],
): boolean => {
  return selectedFilters.some((filter) =>
    options.includes(filter.id as string),
  );
};

const portsOptions = computed(() => {
  const { agencies, tradelanes } = props.modelValue;

  if (!agencies.length && !tradelanes.length) {
    return props.options.ports;
  }

  return props.options.ports.filter((port) => {
    const matchesAgencies = matchesSelection(agencies, port.inAgencies);
    const matchesTradelanes = matchesSelection(tradelanes, port.inTradelanes);

    return agencies.length && tradelanes.length
      ? matchesAgencies && matchesTradelanes
      : matchesAgencies || matchesTradelanes;
  });
});

const tradelaneOptions = computed(() => {
  const { agencies, ports } = props.modelValue;

  if (!agencies.length && !ports.length) {
    return props.options.tradelanes;
  }

  return props.options.tradelanes.filter((tradelane) => {
    const matchesAgencies = matchesSelection(agencies, tradelane.inAgencies);
    const matchesPorts = matchesSelection(ports, tradelane.inPorts);

    return agencies.length && ports.length
      ? matchesAgencies && matchesPorts
      : matchesAgencies || matchesPorts;
  });
});

const agenciesOptions = computed(() => {
  const { tradelanes, ports } = props.modelValue;

  if (!tradelanes.length && !ports.length) {
    return props.options.agencies;
  }

  return props.options.agencies.filter((agency) => {
    const matchesTradelanes = matchesSelection(tradelanes, agency.inTradelanes);
    const matchesPorts = matchesSelection(ports, agency.inPorts);

    return tradelanes.length && ports.length
      ? matchesTradelanes && matchesPorts
      : matchesTradelanes || matchesPorts;
  });
});

const updateQuery = (newQuery: IAdminPortCallsQuery) => {
  query.value.ports = newQuery.ports;
  query.value.tradelanes = newQuery.tradelanes;
  query.value.agencies = newQuery.agencies;
  query.value.period = newQuery.period;
};

const clearActiveFilter = (filter: IFilter) => {
  const newQuery = clearFilter(filter, query);
  updateQuery(newQuery);
};

const clearAllFilters = () => {
  const newQuery = clearQuery(query);
  updateQuery(newQuery);
};
</script>
<template>
  <FiltersLayout class="rounded bg-white drop-shadow" @clear="clearAllFilters">
    <FilterMenuSelectMultiple
      label="Port"
      v-model="query.ports"
      :options="portsOptions"
    />
    <FilterCustomPeriodSelect
      title="Date"
      v-model="query.period"
      :maxDate="null"
    />
    <FilterMenuSelectMultiple
      label="Trade"
      v-model="query.tradelanes"
      :options="tradelaneOptions"
    />
    <FilterMenuSelectMultiple
      label="Agency"
      v-model="query.agencies"
      :options="agenciesOptions"
    />
    <template v-if="activeFilters.length" #tags>
      <FilterTag
        v-for="filter in activeFilters"
        :key="filter.id"
        :name="filter.label"
        @click="clearActiveFilter(filter)"
      />
    </template>
  </FiltersLayout>
</template>
